import { DragDropModule } from '@angular/cdk/drag-drop';
import { AfterViewInit, ChangeDetectorRef, Component, computed, EventEmitter, inject, Input, Output, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { TranslocoModule } from '@jsverse/transloco';
import { produce } from 'immer';
import { Observable } from 'rxjs';
import {
  GuidelineGraphNode,
  GuidelineService,
  GuidelineViewService,
  ScrollService,
  SelectionService,
  TreeScrollService,
  TreeStates,
} from 'shared/domain';
import { ConfirmationDialogComponent, DialogService, ConfirmationDialogArgs, DotComponent } from 'shared/ui-common';

import { ChapterTreeNode, ChapterWidget, NewGuidelineChapter, NodeAddedEvent, NodeDroppedEvent, TreeNode } from '@treetop/shared/model';

import { TreeDragService } from './tree-drag.service';
import { TreeComponent } from '../../../ui-common/src/lib/components/tree/tree.component';

@Component({
  standalone: true,
  imports: [
    MatTreeModule,
    MatButtonModule,
    TranslocoModule,
    DragDropModule,
    MatDialogModule,
    MatTooltipModule,
    MatIconModule,
    TreeComponent,
    DotComponent,
  ],
  selector: 'shared-chapters-tree',
  templateUrl: './chapters-tree.component.html',
  styleUrls: ['./chapters-tree.component.scss'],
  providers: [TreeDragService],
})
export class SharedChaptersTreeComponent implements AfterViewInit {
  private treeScrollService = inject(TreeScrollService);
  private ref = inject(ChangeDetectorRef);
  private dialogService = inject(DialogService);
  protected selectionService = inject(SelectionService);
  private guidelineViewService = inject(GuidelineViewService, { optional: true });
  private scrollService = inject(ScrollService);
  private guidelineService = inject(GuidelineService);

  @Input() public isPublicPage?: boolean;
  @Input() public readonly?: boolean = false;
  @Output() public nodeClicked = new EventEmitter<ChapterTreeNode>();

  protected activeChapter = signal<ChapterTreeNode | undefined>(undefined);

  protected selectedChapters = computed(() => {
    const selectedChapter = this.flattenedChapters().find((chapter) => chapter.id === this.selectionService.selection()?.uniqueId);
    return selectedChapter ? [selectedChapter] : [];
  });

  protected flattenedChapters = computed(() => {
    const chapters = this.chapters();

    const getAllChapters = (items: ChapterTreeNode[]): ChapterTreeNode[] => {
      return items?.reduce((acc: ChapterTreeNode[], item: ChapterTreeNode) => {
        if (item.children?.length > 0) {
          return [...acc, item, ...getAllChapters(item.children as ChapterTreeNode[])];
        }
        return [...acc, item];
      }, []);
    };

    return getAllChapters(chapters);
  });

  protected chapterTreeItems = computed(() => this.chapters() ?? []);

  protected hasResourceTags = computed(() => this.flattenedChapters().some((chapter) => chapter.item?.resourceTags?.length));

  protected chapters = computed(() => {
    const graph = this.guidelineService.graph();
    const map = this.guidelineService.map();
    const numberedChapters = this.guidelineService.numberedChapters();

    const buildChapter = (node: GuidelineGraphNode, parentId: string | null = null): ChapterTreeNode => {
      const chapterWidget = map[node.uniqueId]?.() as NewGuidelineChapter;
      if (!chapterWidget) return null;

      return {
        id: chapterWidget.uniqueId,
        title: chapterWidget.title || '',
        no: numberedChapters[node.uniqueId] ?? '',
        parentId: parentId ?? undefined,
        item: {
          resourceTags: chapterWidget.resourceTags,
        },
        children: node.children
          ?.filter((child) => child.type === 'chapter')
          .map((child: GuidelineGraphNode) => buildChapter(child, node.uniqueId)),
      };
    };

    return graph?.root.children
      ?.filter((child) => child.type === 'chapter')
      .map((child: GuidelineGraphNode) => buildChapter(child, graph.root.uniqueId));
  });

  public ngAfterViewInit(): void {
    this.treeScrollService.activeTreeElementId.subscribe((activeTreeElementId) => {
      if (this.flattenedChapters()?.length > 0) {
        if (activeTreeElementId === TreeStates.First) {
          this.activeChapter.set(this.flattenedChapters()[0]);
        } else if (activeTreeElementId === TreeStates.Last) {
          if (this.flattenedChapters()?.length > 0) {
            this.activeChapter.set(this.flattenedChapters()[this.flattenedChapters().length - 1]);
          }
        } else {
          const activeChapter = this.flattenedChapters().find((chapter) => chapter.id === activeTreeElementId);
          this.activeChapter.set(activeChapter);
        }
        this.ref.detectChanges();
      }
    });
  }

  protected confirmDeletion = (chapter: NewGuidelineChapter): Observable<boolean> => {
    const args: ConfirmationDialogArgs = {
      message: chapter.title ? 'DeleteChapterMessage' : 'DeleteEmptyChapterMessage',
      params: {
        title: chapter.title,
      },
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };

    return this.dialogService
      .open(ConfirmationDialogComponent, {
        icon: 'circle-x',
        title: 'DeleteChapter',
        data: args,
        disableClose: true,
      })
      .afterClosed();
  };

  protected onAddChapter({ node }: NodeAddedEvent<unknown>) {
    this.guidelineService.addChapter({ title: node.title }, node.parentId);
  }

  protected onEditChapter(node: TreeNode<unknown>) {
    const selectedElement = this.flattenedChapters().find((chapter) => chapter.id === node.id);
    if (!selectedElement) return;

    const selectedChapter = this.guidelineService.selectElement<ChapterWidget>(selectedElement.id)();

    const updatedChapter = produce(selectedChapter, (draft) => {
      draft.title = node.title;
    });

    this.guidelineService.updateWidget(selectedElement.id, updatedChapter);
  }

  protected onDeleteChapter(chapter: TreeNode<unknown>) {
    const selectedElement = this.flattenedChapters().find((item) => item.id === chapter.id);
    if (!selectedElement) return;

    this.selectionService.clearSelection();

    this.guidelineService.removeWidget(selectedElement.id);
  }

  protected onNodeDropped(event: NodeDroppedEvent) {
    this.guidelineService.moveChapter(event.node.id, event.newParentChapterId, event.index);
  }

  protected scrollToChapter(node: TreeNode<unknown>) {
    if (!node) {
      return;
    }

    const selectedElement = this.flattenedChapters().find((chapter) => chapter.id === node.id);

    if (!this.guidelineViewService?.isEditImportedGuidelineMode()) {
      this.selectionService.selectWidget(selectedElement.id);
    }

    this.scrollService.scrollTo(selectedElement?.id);

    this.nodeClicked.emit(selectedElement);
  }
}
